import React, { useState } from 'react';
import { Plus, FileText, UserPlus, Upload, X } from 'lucide-react';
import { useAlert } from '../../contexts/AlertContext';

interface FloatingActionButtonProps {
  onNewDemand: () => void;
  onNewContact?: () => void;
  onUploadDocument?: () => void;
  canCreate?: boolean;
}

export const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  onNewDemand,
  onNewContact,
  onUploadDocument,
  canCreate = true,
}) => {
  const { showAlert } = useAlert();
  const [isOpen, setIsOpen] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Safe render check - ensure we never return early before hooks
  if (!canCreate) {
    return null;
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      showAlert('info', 'Aviso', `Arquivo ${file.name} selecionado.`);
    }
    e.target.value = '';
  };

  return (
    <>
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileUpload} 
        className="hidden" 
        accept=".pdf,image/*,.doc,.docx,.xls,.xlsx"
      />
      
      {isOpen ? (
        <div
          className="md:hidden fixed inset-0 bg-black/30 z-30 fade-backdrop"
          onClick={() => setIsOpen(false)}
        />
      ) : null}

      <div className="md:hidden fixed bottom-[76px] right-5 z-40 flex flex-col-reverse items-end gap-3">
        {isOpen ? (
          <>
            <div className="fab-item flex items-center gap-3">
              <div className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-2 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
                Novo Contato
              </div>
              <button
                onClick={() => { setIsOpen(false); onNewContact?.(); }}
                className="w-11 h-11 rounded-full shadow-lg flex items-center justify-center transition-all bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                <UserPlus className="w-4 h-4" />
              </button>
            </div>

            <div className="fab-item flex items-center gap-3">
              <div className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-2 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
                Upload de Documento
              </div>
              <button
                onClick={() => {
                  setIsOpen(false);
                  if (onUploadDocument) onUploadDocument();
                  else fileInputRef.current?.click();
                }}
                className="w-11 h-11 rounded-full shadow-lg flex items-center justify-center transition-all bg-violet-600 hover:bg-violet-500 text-white"
              >
                <Upload className="w-4 h-4" />
              </button>
            </div>

            <div className="fab-item flex items-center gap-3">
              <div className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-2 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
                Nova Demanda
              </div>
              <button
                onClick={() => { setIsOpen(false); onNewDemand(); }}
                className="w-11 h-11 rounded-full shadow-lg flex items-center justify-center transition-all bg-blue-600 hover:bg-blue-500 text-white"
              >
                <FileText className="w-4 h-4" />
              </button>
            </div>
          </>
        ) : null}

        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 ${
            isOpen ? 'bg-slate-700 dark:bg-slate-800 rotate-45' : 'bg-blue-600 hover:bg-blue-500 fab-pulse'
          }`}
          style={{ boxShadow: '0 6px 24px rgba(21, 101, 192, 0.4)' }}
          aria-label="Ações rápidas"
        >
          {isOpen ? <X className="w-6 h-6 text-white" /> : <Plus className="w-6 h-6 text-white" />}
        </button>
      </div>
    </>
  );
};
